import { call, put, takeLatest, all, delay } from 'redux-saga/effects';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  fetchCarouselRequest,
  fetchCarouselSuccess,
  fetchCarouselFailure,
  fetchSingleCarouselRequest,
  fetchSingleCarouselSuccess,
  fetchSingleCarouselFailure,
  toggleCarouselStatusRequest,
  toggleCarouselStatusSuccess,
  toggleCarouselStatusFailure,
  deleteCarouselRequest,
  deleteCarouselSuccess,
  deleteCarouselFailure,
  deleteSlideRequest,
  deleteSlideSuccess,
  deleteSlideFailure,
  addCarouselLocationRequest,
  addCarouselLocationSuccess,
  addCarouselLocationFailure,
  deleteCarouselLocationRequest,
  deleteCarouselLocationSuccess,
  deleteCarouselLocationFailure,
} from '../slices/CarouselSlice';
import { getUiArrangementRequest } from '../settings/settingsSlice';

// --- API Calls ---
const fetchCarouselAPI = async (payload) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/carouselcreate`,
    payload
  );
  return response.data.body;
};

const fetchSingleCarouselAPI = async (id) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/getcarouselslidedetails/${id}`
  );
  return response.data.body;
};

const deleteCarouselAPI = async (id) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/slidedelete/${id}`
  );
  return response.data;
};

const deleteSlideAPI = async (id) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/slidedelete/${id}`
  );
  return response.data;
};

const addCarouselLocationAPI = async (payload) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/addlocation`,
    payload
  );
  return response.data;
};

const deleteCarouselLocationAPI = async (id) => {
  const response = await axios.delete(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/deletelocation/${id}`
  );
  return response.data;
};

// --- Saga Workers ---
function* handleFetchCarousel(action) {
  try {
    const response = yield call(fetchCarouselAPI, action.payload);
    yield put(fetchCarouselSuccess(response || []));
  } catch (error) {
    yield put(fetchCarouselFailure(error.message || 'Failed to fetch carousel list'));
  }
}

function* handleFetchSingleCarousel(action) {
  try {
    const response = yield call(fetchSingleCarouselAPI, action.payload);
    yield put(fetchSingleCarouselSuccess(response));
  } catch (error) {
    yield put(fetchSingleCarouselFailure(error.message || 'Failed to fetch carousel'));
  }
}

function* toggleCarouselStatusSaga(action) {
  try {
    const { slideId, enabled, carouselId } = action.payload;

    const response = yield call(axios.post, `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/slidestatus`, {
      id: slideId,
      enabled,
    });

    if (response?.data?.success) {
      yield put(toggleCarouselStatusSuccess({ slideId, enabled }));
      yield delay(300);

      if (carouselId) {
        yield put(fetchSingleCarouselRequest(carouselId));
      }

      yield put({ type: 'carousel/clearLoading', payload: slideId });

      yield call(toast.success, response.data.message || `Slide has been ${enabled ? 'enabled' : 'disabled'} successfully.`);
    } else {
      yield put(toggleCarouselStatusFailure(response.data.message || 'Toggle failed'));
      yield call(toast.error, response.data.message || 'Failed to update slide status.');
    }
  } catch (error) {
    yield put(toggleCarouselStatusFailure(error.message || 'Toggle failed'));
    yield call(toast.error, error.message || 'Something went wrong while toggling status.');
  }
}

function* handleDeleteCarousel(action) {
  try {
    const id = action.payload;
    yield call(deleteCarouselAPI, id);
    yield put(deleteCarouselSuccess(id));
    yield put(fetchCarouselRequest());
    yield call(toast.success, 'Carousel deleted successfully');
  } catch (error) {
    yield put(deleteCarouselFailure(error.message || 'Failed to delete carousel'));
    yield call(toast.error, error.message || 'Something went wrong while deleting carousel.');
  }
}

function* handleDeleteSlide(action) {
  console.log("action", action)
  try {
    const { id } = action.payload;

    const response = yield call(deleteSlideAPI, id);
    if (response?.success) {
      yield put(deleteSlideSuccess(id));
      // yield call(toast.success, response.message || 'Slide deleted successfully');
    } else {
      yield put(deleteSlideFailure(response.message || 'Failed to delete slide'));
      yield call(toast.error, response.message || 'Failed to delete slide');
    }
  } catch (error) {
    yield put(deleteSlideFailure(error.message || 'Slide deletion failed'));
    yield call(toast.error, error.message || 'Something went wrong while deleting slide.');
  }
}

function* handleAddCarouselLocation(action) {
  try {
    const response = yield call(addCarouselLocationAPI, action.payload);
    if (response?.success) {
      yield put(addCarouselLocationSuccess(response.body));
      yield put(fetchCarouselRequest());
      yield put(getUiArrangementRequest());
      yield call(toast.success, response.message || 'Location added successfully');
    } else {
      yield put(addCarouselLocationFailure(response.message || 'Failed to add location'));
      yield call(toast.error, response.message || 'Failed to add location');
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || error.message || 'Failed to add location';
    yield put(addCarouselLocationFailure(message));
    yield call(toast.error, message);
  }
}

function* handleDeleteCarouselLocation(action) {
  try {
    const id = action.payload;
    const response = yield call(deleteCarouselLocationAPI, id);
    if (response?.success) {
      yield put(deleteCarouselLocationSuccess(id));
      yield put(fetchCarouselRequest());
      yield put(getUiArrangementRequest());
      yield call(toast.success, response.message || 'Location deleted successfully');
    } else {
      yield put(deleteCarouselLocationFailure(response.message || 'Failed to delete location'));
      yield call(toast.error, response.message || 'Failed to delete location');
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || error.message || 'Failed to delete location';
    yield put(deleteCarouselLocationFailure(message));
    yield call(toast.error, message);
  }
}

// --- Root saga ---
export default function* carouselSaga() {
  yield all([
    takeLatest(fetchCarouselRequest.type, handleFetchCarousel),
    takeLatest(fetchSingleCarouselRequest.type, handleFetchSingleCarousel),
    takeLatest(toggleCarouselStatusRequest.type, toggleCarouselStatusSaga),
    takeLatest(deleteCarouselRequest.type, handleDeleteCarousel),
    takeLatest(deleteSlideRequest.type, handleDeleteSlide),
    takeLatest(addCarouselLocationRequest.type, handleAddCarouselLocation),
    takeLatest(deleteCarouselLocationRequest.type, handleDeleteCarouselLocation),
  ]);
}
